import { Box, Typography, Container, Card, CardMedia, CardContent, Link } from '@mui/material';
import Grid from '@mui/material/Grid2';

function MainPage() {
  return (
    <>
      {/* Introduction Section */}
      <Container sx={{ padding: '40px 0' }}>
        {/* Your Name in Larger Font */}
        <Typography variant="h2" sx={{ textAlign: 'left', marginBottom: '24px' }}>
          Olga Vechtomova
        </Typography>
        <Typography variant="body1" sx={{ fontSize: '1.2rem', textAlign: 'left' }}>
          Olga Vechtomova is a Professor at the University of Waterloo, working at the intersection of Natural Language Processing, Artificial Intelligence, art, music and creativity. Her lab designs generative AI systems that act as creative partners: generating poetic lines and evolving soundscapes in response to live instrumental music, and enabling dancers to shape musical environments through movement. Her recent research explores how steering the internal representations of language models can produce genuinely surprising, evocative narrative, and draws on philosophy and literary theory to ask when AI-generated text goes beyond performing novelty. Her work has been recognized with a Best Paper Award at ICCC 2025 and is supported by SSHRC and NSERC.
        </Typography>
      </Container>

      {/* Featured Projects Section */}
      <Container sx={{ padding: '40px 0' }}>
        <Typography variant="h4" gutterBottom sx={{ textAlign: 'left' }}>
          Featured Projects
        </Typography>
        <Grid container spacing={4}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Card sx={{ display: 'flex', flexDirection: 'column', cursor: 'pointer' }}>
              <Link href="/activation-steering" sx={{ textDecoration: 'none', color: 'inherit' }}>
                <CardMedia
                  component="img"
                  image="/images/ActivationSteering.jpg"
                  alt="Activation Steering for Creative Estrangement"
                  sx={{ height: '250px', width: '100%', objectFit: 'cover', opacity: 0.9 }}
                />
              </Link>
              <CardContent>
                <Typography variant="body1" sx={{ textAlign: 'left', fontWeight: 'bold' }}>
                  <Link href="/activation-steering" sx={{ textDecoration: 'none', color: 'inherit' }}>
                    Activation Steering for Creative Estrangement
                  </Link>
                </Typography>
                <Typography variant="body2" sx={{ textAlign: 'left', marginTop: '10px' }}>
                  Generating evocative narrative through controlled disruption of language models. By steering a model's internal representations during generation, the approach produces emergent literary qualities absent from its training data. Published as a long paper at the International Conference on Computational Creativity (ICCC 2026).
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Card sx={{ display: 'flex', flexDirection: 'column', cursor: 'pointer' }}>
              <Link href="/lyotard-sublime" sx={{ textDecoration: 'none', color: 'inherit' }}>
                <CardMedia
                  component="img"
                  image="/images/LyotardSublime.jpg"
                  alt="Is It Happening? Lyotard's Sublime and the Indeterminate in AI-Generated Text"
                  sx={{ height: '250px', width: '100%', objectFit: 'cover', opacity: 0.9 }}
                />
              </Link>
              <CardContent>
                <Typography variant="body1" sx={{ textAlign: 'left', fontWeight: 'bold' }}>
                  <Link href="/lyotard-sublime" sx={{ textDecoration: 'none', color: 'inherit' }}>
                    Is It Happening? Lyotard's Sublime and the Indeterminate in AI-Generated Text
                  </Link>
                </Typography>
                <Typography variant="body2" sx={{ textAlign: 'left', marginTop: '10px' }}>
                  A framework grounded in Jean-François Lyotard's aesthetics of the sublime for distinguishing AI-generated text that performs the appearance of novelty from text in which something genuinely unforeseen has arisen. Published as a short paper at the International Conference on Computational Creativity (ICCC 2026).
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Card sx={{ display: 'flex', flexDirection: 'column', cursor: 'pointer' }}>
              <Link href="/lyricjam-sonic" sx={{ textDecoration: 'none', color: 'inherit' }}>
                <CardMedia
                  component="img"
                  image="/images/LyricJamSonicMode.jpg"
                  alt="LyricJam Sonic"
                  sx={{ height: '250px', width: '100%', objectFit: 'cover', opacity: 0.9 }}
                />
              </Link>
              <CardContent>
                <Typography variant="body1" sx={{ textAlign: 'left', fontWeight: 'bold' }}>
                  <Link href="/lyricjam-sonic" sx={{ textDecoration: 'none', color: 'inherit' }}>
                    LyricJam Sonic
                  </Link>
                </Typography>
                <Typography variant="body2" sx={{ textAlign: 'left', marginTop: '10px' }}>
                  LyricJam Sonic is a generative system that creates evolving soundscapes in real-time to complement live music performances. It uses AI to analyze the music and generate soundscapes that adapt to the mood and dynamics of the performance.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Card sx={{ display: 'flex', flexDirection: 'column', cursor: 'pointer' }}>
              <Link href="/lyricjam" sx={{ textDecoration: 'none', color: 'inherit' }}>
                <CardMedia
                  component="img"
                  image="/images/LyricJamMode.jpg"
                  alt="LyricJam"
                  sx={{ height: '250px', width: '100%', objectFit: 'cover', opacity: 0.9 }}
                />
              </Link>
              <CardContent>
                <Typography variant="body1" sx={{ textAlign: 'left', fontWeight: 'bold' }}>
                  <Link href="https://lyricjam.ai" target="_blank" rel="noopener" sx={{ textDecoration: 'none', color: 'inherit' }}>
                    LyricJam
                  </Link>
                </Typography>
                <Typography variant="body2" sx={{ textAlign: 'left', marginTop: '10px' }}>
                  LyricJam is an AI-driven system that generates lyrics in real-time during live instrumental performances. It listens to the music and creates lyrics that match the rhythm, melody, and emotional tone of the performance.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Card sx={{ display: 'flex', flexDirection: 'column', cursor: 'pointer' }}>
              <Link href="/reimagining-dance" sx={{ textDecoration: 'none', color: 'inherit' }}>
                <Box sx={{ position: 'relative', height: '250px' }}>
                  <CardMedia
                    component="img"
                    image="/images/ReimaginingDance.jpg"
                    alt="Reimagining Dance"
                    sx={{
                      height: '100%',
                      width: '100%',
                      objectFit: 'cover',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      transform: 'scaleX(-1)'
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'linear-gradient(to right, rgba(0,0,0,1), rgba(0,0,0,0))',
                      zIndex: 1
                    }}
                  />
                </Box>
              </Link>
              <CardContent>
                <Typography variant="body1" sx={{ textAlign: 'left', fontWeight: 'bold' }}>
                  <Link href="/reimagining-dance" sx={{ textDecoration: 'none', color: 'inherit' }}>
                    Reimagining Dance
                  </Link>
                </Typography>
                <Typography variant="body2" sx={{ textAlign: 'left', marginTop: '10px' }}>
                  An innovative system that enables dancers to dynamically shape musical environments through their movements, creating a bidirectional creative partnership between human performers and AI systems in real-time.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Card sx={{ display: 'flex', flexDirection: 'column', cursor: 'pointer' }}>
              <Link href="/balancing-indeterminacy" sx={{ textDecoration: 'none', color: 'inherit' }}>
                <Box sx={{ position: 'relative', height: '250px' }}>
                  <CardMedia
                    component="img"
                    image="/images/Balancing.jpg"
                    alt="Balancing Indeterminacy and Structure"
                    sx={{
                      height: '100%',
                      width: '100%',
                      objectFit: 'cover',
                      position: 'absolute',
                      top: 0,
                      left: 0
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'linear-gradient(to right, rgba(0,0,0,1), rgba(0,0,0,0))',
                      zIndex: 1
                    }}
                  />
                </Box>
              </Link>
              <CardContent>
                <Typography variant="body1" sx={{ textAlign: 'left', fontWeight: 'bold' }}>
                  <Link href="/balancing-indeterminacy" sx={{ textDecoration: 'none', color: 'inherit' }}>
                    Balancing Indeterminacy and Structure
                  </Link>
                </Typography>
                <Typography variant="body2" sx={{ textAlign: 'left', marginTop: '10px' }}>
                  Exploring how neural text generative models can produce poetic lines that serve as seeds for artistic inspiration, comparing LSTM-VAE and Transformer-based approaches for creative text generation.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          {/* Add more projects as needed */}
        </Grid>
      </Container>
    </>
  );
}

export default MainPage;